import { Component, ElementRef, ViewChild, input, output, signal } from '@angular/core';
import Cropper from 'cropperjs';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { EPhotoContentType, IUploadImageOutput } from 'src/models/common.model';

@Component({
  selector: 'liberro-img-uploader',
  standalone: false,
  templateUrl: './liberro-img-uploader.component.html',
  styleUrl: './liberro-img-uploader.component.less'
})
export class LiberroImgUploaderComponent {

  public cropSize = input<{ width: number; height: number }>({ width: 300, height: 300 });
  public supportedTypes = input<EPhotoContentType[]>([
    EPhotoContentType.JPEG,
    EPhotoContentType.PNG,
    EPhotoContentType.WEBP
  ]);
  protected readonly acceptTypes = Object.values(EPhotoContentType).join(',');
  public maxSizeBytes = input.required<number>();
  public errorType = output<void>();
  public errorResolution = output<void>();
  public errorSize = output<void>();
  public imageReady = output<IUploadImageOutput>();

  @ViewChild('imgUpload') public imgUpload?: ElementRef<HTMLImageElement>;

  public uploadedImage = signal<string | null>(null);
  public isModalVisible = signal(false);
  public imageUrl = signal<string | null>(null);
  public cropper = signal<Cropper | null>(null);
  private zooomDefault = 1;
  private rotateDefault = 0;
  public zoomValue = signal(this.zooomDefault);
  public rotateValue = signal(this.rotateDefault);
  public imageContentType = signal<EPhotoContentType | null>(null);
  public imageFileName = signal<string | null>(null);

  public beforeUpload = (file: NzUploadFile): boolean => {
    const reader = new FileReader();
    reader.onload = () => {
      this.imageUrl.set(reader.result as string);
      const type = file.type;
      const supportedTypes = this.supportedTypes();
      if (type && supportedTypes.includes(type as EPhotoContentType)) {
        this.imageContentType.set(type as EPhotoContentType);
        this.imageFileName.set(file.name || null);
        this.isModalVisible.set(true);
        setTimeout(() => this.initCropper());
      } else {
        this.errorType.emit();
        this.handleCancel();
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reader.readAsDataURL(file as any);
    return false;
  };

  public initCropper(): void {
    const image = this.imgUpload?.nativeElement;
    if (image && !this.cropper()) {
      // https://github.com/fengyuanchen/cropperjs/blob/v1/README.md
      const cropperInstance = new Cropper(image, {
        viewMode: 1,
        aspectRatio: 1,
        autoCropArea: 1,
        dragMode: 'move',
        cropBoxMovable: true,
        cropBoxResizable: false,
        zoomable: true,
        zoomOnWheel: false,
        scalable: false,
        movable: true,
        guides: false,
        ready: () => {
          if (cropperInstance.getImageData().naturalWidth < this.cropSize().width ||
            cropperInstance.getImageData().naturalHeight < this.cropSize().height) {
            this.errorResolution.emit();
            this.handleCancel();
          }
          cropperInstance.setCropBoxData({
            width: this.cropSize().width,
            height: this.cropSize().height
          });
        }
      });
      this.cropper.set(cropperInstance);
    }
  }

  public onZoomChange(value: number): void {
    this.zoomValue.set(value);
    const cropper = this.cropper();
    if (cropper) {
      const imageData = cropper.getImageData();
      const isLandscape = imageData.naturalWidth > imageData.naturalHeight;
      const zoomRatio = isLandscape
        ? this.cropSize().width / imageData.naturalWidth
        : this.cropSize().height / imageData.naturalHeight;
      cropper.zoomTo(value * zoomRatio);
    }
  }

  public onRotateChange(value: number): void {
    this.rotateValue.set(value);
    const cropper = this.cropper();
    if (cropper) {
      cropper.rotateTo(value);
    }
  }

  public handleConfirm(): void {
    const cropper = this.cropper();
    if (cropper) {
      const canvas = cropper.getCroppedCanvas({ width: this.cropSize().width, height: this.cropSize().height });
      const contentType = this.imageContentType();
      const fileName = this.imageFileName();
      canvas.toBlob((b) => {
        if (b && contentType) {
          if (b.size > this.maxSizeBytes()) {
            this.errorSize.emit();
            this.handleCancel();
          } else {
            this.imageReady.emit({
              image: b,
              contentType,
              fileName: fileName || undefined
            });
          }
        }
      });
    }
    this.isModalVisible.set(false);
    this.zoomValue.set(this.zooomDefault);
    this.rotateValue.set(this.rotateDefault);
    cropper?.destroy();
    this.cropper.set(null);
  }

  public handleCancel(): void {
    this.isModalVisible.set(false);
    this.zoomValue.set(this.zooomDefault);
    this.rotateValue.set(this.rotateDefault);
    this.imageFileName.set(null);
    const cropper = this.cropper();
    cropper?.destroy();
    this.cropper.set(null);
  }

}